import { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabaseShowTimes, supabaseMovies } from '../supabase';
import { THEATERS, getTicketLink } from '../constants';
import { slugify } from '../utils/slugify';
import WatchlistHeart from '../components/WatchlistHeart';

const SUPPORTED_FORMATS = ['35mm', '70mm', '16mm'];

const PAGE_META = {
  h1: '35mm Screenings in NYC',
  metaTitle: '35mm Screenings in NYC | Every Film Print Showing | Film Revival NYC',
  metaDescription: 'Every upcoming 35mm, 70mm, and 16mm film print screening across NYC revival cinemas. Updated daily.',
  intro: `Every upcoming 35mm, 70mm, and 16mm film print screening across NYC's revival cinemas — updated daily.`,
};

function getYearFromMovie(movie) {
  if (!movie?.release_dates) return null;
  try {
    const d = typeof movie.release_dates === 'string' ? JSON.parse(movie.release_dates) : movie.release_dates;
    if (!d || typeof d !== 'object') return null;
    const dates = Object.values(d).flat().map(r => r?.date).filter(Boolean).sort();
    if (dates.length > 0) return dates[0].slice(0, 4);
  } catch { /* skip */ }
  return null;
}

const createGoogleCalendarLink = (movieTitle, theater, startTime) => {
  const eventDate = new Date(startTime);
  const endDate = new Date(eventDate.getTime() + (3 * 60 * 60 * 1000));
  const startDateTime = eventDate.toISOString().replace(/[:-]/g, '').replace('.000', 'Z');
  const endDateTime = endDate.toISOString().replace(/[:-]/g, '').replace('.000', 'Z');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${movieTitle} at ${theater}`,
    details: `Movie showing of ${movieTitle} at ${theater}`,
    location: theater,
    dates: `${startDateTime}/${endDateTime}`,
  });
  return `https://www.google.com/calendar/render?${params.toString()}`;
};

export default function FormatPage() {
  const meta = PAGE_META;
  const [showtimes, setShowtimes] = useState([]);
  const [moviesById, setMoviesById] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const now = new Date();
      const end = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
      const all = await supabaseShowTimes.getByFormatsWithinTimeFrame(SUPPORTED_FORMATS, now.toISOString(), end.toISOString());
      const filtered = (all || []).filter(st => !st.isBlocked);

      const uniqueIds = [...new Set(filtered.map(st => st.movie_id))];
      const movies = uniqueIds.length > 0 ? await supabaseMovies.getByIds(uniqueIds) : [];
      const byId = {};
      (movies || []).forEach(m => { byId[m.id] = m; });

      if (!cancelled) {
        setShowtimes(filtered);
        setMoviesById(byId);
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Group showtimes by date (YYYY-MM-DD in ET)
  const grouped = useMemo(() => {
    const byDay = {};
    for (const st of showtimes) {
      const d = new Date(st.start_at);
      const dayKey = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // YYYY-MM-DD
      if (!byDay[dayKey]) byDay[dayKey] = [];
      byDay[dayKey].push(st);
    }
    return byDay;
  }, [showtimes]);

  const sortedDays = Object.keys(grouped).sort();
  const canonicalUrl = 'https://filmrevivalnyc.com/35mm-nyc';
  const ogImage = 'https://filmrevivalnyc.com/FILM_REVIVAL_NY_OG.jpg';

  // JSON-LD: ItemList of ScreeningEvents
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': meta.h1,
    'itemListElement': showtimes.slice(0, 50).map((st, i) => {
      const movie = moviesById[st.movie_id];
      const year = getYearFromMovie(movie);
      const ticketUrl = getTicketLink(st.booking_link, THEATERS.find(t => t.name === st.theater)?.id);
      return {
        '@type': 'ListItem',
        'position': i + 1,
        'item': {
          '@type': 'ScreeningEvent',
          'name': `${st.cinema_movie_title || movie?.title} at ${st.theater}`,
          'startDate': st.start_at,
          'location': { '@type': 'MovieTheater', 'name': st.theater },
          'workPresented': {
            '@type': 'Movie',
            'name': movie?.title || st.cinema_movie_title,
            ...(year && { 'dateCreated': year }),
          },
          ...(ticketUrl && {
            'offers': {
              '@type': 'Offer',
              'url': ticketUrl,
              'availability': 'https://schema.org/InStock',
            }
          })
        }
      };
    })
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://filmrevivalnyc.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': '35mm', 'item': canonicalUrl }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{meta.metaTitle}</title>
        <meta name="description" content={meta.metaDescription} />
        <meta property="og:title" content={meta.metaTitle} />
        <meta property="og:description" content={meta.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.metaTitle} />
        <meta name="twitter:description" content={meta.metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
        <div style={{ marginBottom: '20px' }}>
          <Link to="/" style={{ color: '#8b6914', textDecoration: 'none', fontSize: '0.9rem' }}>
            ← Back to calendar
          </Link>
        </div>

        <h1 style={{ fontFamily: "'Cinzel', serif", color: '#1a1714', fontSize: '2.2rem', marginBottom: '8px' }}>
          {meta.h1}
        </h1>
        <div style={{ width: '48px', height: '2px', background: '#d4a843', margin: '8px 0 20px', borderRadius: '1px' }} />
        <p style={{ color: '#3d3630', fontSize: '1rem', lineHeight: '1.6', marginBottom: '32px' }}>
          {meta.intro}
        </p>

        {loading ? (
          <p style={{ color: '#6b6255', fontStyle: 'italic', textAlign: 'center' }}>Loading screenings…</p>
        ) : showtimes.length === 0 ? (
          <p style={{ color: '#6b6255', fontStyle: 'italic', textAlign: 'center' }}>
            No upcoming film print screenings on the calendar right now. Check back soon.
          </p>
        ) : (
          <>
            <p style={{ color: '#6b6255', fontSize: '0.9rem', marginBottom: '16px' }}>
              {showtimes.length} upcoming screening{showtimes.length === 1 ? '' : 's'} across {new Set(showtimes.map(s => s.theater)).size} theaters
            </p>
            {sortedDays.map(day => {
              const dayDate = new Date(day + 'T12:00:00');
              const dayLabel = dayDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'America/New_York' });
              return (
                <section key={day} style={{ marginBottom: '32px' }}>
                  <h2 style={{ fontFamily: "'Cinzel', serif", fontSize: '1.2rem', color: '#1a1714', borderBottom: '1px solid #d4cec4', paddingBottom: '8px', marginBottom: '14px' }}>
                    {dayLabel}
                  </h2>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {grouped[day].map(st => {
                      const movie = moviesById[st.movie_id];
                      const year = getYearFromMovie(movie);
                      const title = movie?.title || st.cinema_movie_title;
                      const slug = movie?.slug || (title ? slugify(title, year) : null);
                      const time = new Date(st.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
                      const ticketUrl = getTicketLink(st.booking_link, THEATERS.find(t => t.name === st.theater)?.id);
                      return (
                        <li key={st.id} style={{ padding: '10px 0', borderBottom: '1px solid #ede8e0', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{ minWidth: '64px', color: '#2a2520', fontWeight: 600, fontFamily: "'Cinzel', serif" }}>{time}</span>
                          <span style={{ flex: 1, minWidth: '200px' }}>
                            {slug ? (
                              <Link to={`/films/${slug}`} style={{ color: '#8b6914', textDecoration: 'none', fontWeight: 600 }}>
                                {title}{year && ` (${year})`}
                              </Link>
                            ) : (
                              <span style={{ color: '#2a2520', fontWeight: 600 }}>{title}</span>
                            )}
                            <span style={{ color: '#6b6255', fontSize: '0.85rem', marginLeft: '8px' }}>at {st.theater}</span>
                            {st.film_format && (
                              <span style={{ marginLeft: '8px', fontSize: '0.7rem', color: '#8b6914', border: '1px solid #d4a843', borderRadius: '3px', padding: '1px 5px', fontWeight: 600 }}>
                                {st.film_format}
                              </span>
                            )}
                          </span>
                          {title && <WatchlistHeart movieTitle={title} movieId={st.movie_id} size={16} />}
                          {ticketUrl && (
                            <a href={ticketUrl} target="_blank" rel="noopener noreferrer" className="action-btn tickets-btn" style={{ background: '#8b6914', color: '#fff', padding: '4px 10px', borderRadius: '4px', textDecoration: 'none', fontSize: '0.8rem' }}>
                              Tickets
                            </a>
                          )}
                          <a href={createGoogleCalendarLink(title || 'Film', st.theater, st.start_at)} target="_blank" rel="noopener noreferrer" className="action-btn calendar-btn" style={{ background: '#5a7a9a', color: '#fff', padding: '4px 10px', borderRadius: '4px', textDecoration: 'none', fontSize: '0.8rem' }}>
                            Add to Calendar
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </>
        )}
      </div>
    </>
  );
}
